import type { GridColDef } from '@mui/x-data-grid-premium';
import type { LexicalEditor } from 'lexical';
import { isEmpty, set } from 'lodash';
import type { Attachment, FieldInput, ProductionImportInput } from 'sr-types/lib/production/v1/graphql';
import type { ReferenceInput } from 'sr-types/lib/search/v1/graphql';
import type { Attribute } from '../attributes/attributes';
import { AvailableTypeEnum } from '../attributes/attributes';
import { OWNER_ORGANIZATION_NAME } from '../common/constants/constants';
import { generateAttachmentReferencesHtml } from '../common/lexical/attachmentReferenceEditorUtils';
import { SUPPORTED_FIELD_TYPES_FOR_IMPORT } from './bulkSaveProduction.constants';
import { MATERIAL_COLUMN } from './helpers/productionImportUtils';
import { getCustomFieldNameForImport } from './helpers/productionUtils';
import { ALT_ID_PREFIX, MANAGED_PRODUCTION, OWNER_ORG_ID } from './productionConstants';
export type { DoSaveParamsWithoutManaged } from './bulkSaveProduction.types';


// Normalize raw cell values into the expected JSON type before building ProductionInput.attributes.
const coerceByFieldType = (raw: any, fieldType?: string) => {
    if (!raw) return undefined;

    // TODO: add additional coercion rules as import support expands to more field types.
    if (!SUPPORTED_FIELD_TYPES_FOR_IMPORT.includes(fieldType as AvailableTypeEnum)) {
        return undefined;
    }

    // Default: convert to string
    return String(raw).trim();
};

export const rowsToProduction = (
    rows: Record<string, any>[],
    colDefs: GridColDef[],
    isManagedProduction = false,
    defaultOwnerOrganizationId = '',
    fieldDefinitionReferences: Attribute[] = [],
    state: string = '',
    attachmentsByRow: Map<string, Attachment[]> = new Map(),
    attachmentReferenceEditor?: LexicalEditor
): ProductionImportInput[] => {
    const alternateIdTypes = colDefs
        .filter((col) => col.field.startsWith(ALT_ID_PREFIX))
        .map((col) => col.headerName)
        .filter((headerName): headerName is string => Boolean(headerName));

    return rows.map((row) => {
        const productionInput: ProductionImportInput = {
            publicTitle: row.publicTitle,
            summary: {
                headline: row?.headline ?? '',
                about: row.summary
            },
            productionStatus: row.productionStatus,
            projectStatus: row?.projectStatus ?? '',
            productionType: row.productionType,
            productionGroup: {
                label: row.productionGroup
            },
            releaseDate: row.releaseDate,
            productionYears: row.productionYears,
            genre: isEmpty(row.genre) ? [] : row.genre?.split(';'),
            tags: isEmpty(row.tags) ? [] : row.tags?.split(';'),
            alternateIds: [],
            showAttributes: [],
            attributes: [],
            state,
            associatedFieldDefinitionReferences: [],
            currentStatusNote: row.currentStatusNote ?? '',
            associatedPeopleString: row.associatedPeopleString ?? '',
            associatedOrgsString: row.associatedOrgsString ?? '',
            projectOwnerContactsString: row.projectOwnerContactsString ?? '',
            submittedToString: row.submittedToString ?? '',
            sendersString: row.sendersString ?? '',
            writersString: row.writersString ?? '',
            readersString: row.readersString ?? '',
            productionSubType: row.productionSubType ?? '',
            group: row.group ?? ''
        };

        if (row.ID) set(productionInput, 'identity', { id: row.ID });
        if (row.otherTitles)
            set(productionInput, 'otherTitles', [
                {
                    key: 'AKA',
                    value: row.otherTitles
                }
            ]);

        let materialAttributeValue = '';
        const rowAttachmentData: Attachment[] = attachmentsByRow.get(row.id) ?? [];

        if (rowAttachmentData?.length && attachmentReferenceEditor) {
            const references = rowAttachmentData
                .filter((item) => item.entityReference)
                .map((item) => ({
                    ...item.entityReference,
                    attachmentType: item.isLink ? 'link' : 'attachment'
                }));
            const referencesString = JSON.stringify(references);
            set(productionInput, 'materialString', referencesString);

            const attachments = rowAttachmentData
                .filter((item) => item.entityReference)
                .map((item, i) => ({
                    id: `attachment_id_${i + 1}`,
                    name: `attachment_label_${i + 1}`,
                    isLink: item.isLink ? true : false,
                    type: null
                }));

            const attachmentHtml = generateAttachmentReferencesHtml(attachments, attachmentReferenceEditor);
            materialAttributeValue = attachmentHtml;
        }

        if (row.runtime) productionInput.showAttributes.push({ key: 'runtime', value: row.runtime });
        if (row.seasonNo) productionInput.showAttributes.push({ key: 'seasonNo', value: row.seasonNo });
        if (row.episodeNo) productionInput.showAttributes.push({ key: 'episodeNo', value: row.episodeNo });
        if (row.noOfEpisodes) productionInput.showAttributes.push({ key: 'noOfEpisodes', value: row.noOfEpisodes });

        if (isManagedProduction) {
            set(productionInput, MANAGED_PRODUCTION, true);
            if (!row.ownerOrganizationName) {
                set(productionInput, OWNER_ORG_ID, defaultOwnerOrganizationId);
            } else {
                set(productionInput, OWNER_ORGANIZATION_NAME, row.ownerOrganizationName);
            }
        }

        alternateIdTypes.forEach((type) => {
            if (row[`${ALT_ID_PREFIX}${type}`]) {
                const alternateIdParts = row[`${ALT_ID_PREFIX}${type}`].split(';');
                productionInput.alternateIds.push({
                    label: type,
                    value: alternateIdParts[0],
                    url: alternateIdParts[1]
                });
            }
        });

        const attributes: FieldInput[] = [];

        const associatedFieldDefinitionReferences: ReferenceInput[] = [];

        fieldDefinitionReferences.forEach((field) => {
            let rowValue = row?.[field.name];
            if (field.fieldType === AvailableTypeEnum.reference) {
                const refFieldName = getCustomFieldNameForImport(field.name);
                rowValue = row?.[refFieldName];
            }
            // Handle material attribute value if present and null if not present.
            if (field.fieldType === AvailableTypeEnum.note && field.name === MATERIAL_COLUMN) {
                rowValue = materialAttributeValue ?? null;
            }
            const value = coerceByFieldType(rowValue, field.fieldType);
            if (value === undefined) return;

            attributes.push({
                fieldDefinitionReference: field.fieldDefinitionReference,
                fieldType: field.fieldType,
                name: field.name,
                value
            });

            associatedFieldDefinitionReferences.push(field.fieldDefinitionReference);
        });

        if (attributes.length > 0) {
            productionInput.attributes = attributes;
            productionInput.associatedFieldDefinitionReferences = associatedFieldDefinitionReferences;
        }

        return productionInput;
    });
};
